import './style.css';

import { Button } from 'bz-core-controls';
import { observer } from 'mobx-react';
import Moment from 'moment';
import React from 'react';
import { EditorBlock, Entity } from 'tm2-draft-js';

const isArray = (arr) => arr && arr.push && arr.map;
const neverField = '__never__';
const neverOperator = 'never';

@observer
class Group extends React.Component {
  render() {
    let { store, key } = this.props.blockProps;
    let content;
    let entity = Entity.get(key);
    let attr = entity.getData();
    let numberRange = this.getNumberRange(attr.operatorValue);
    const isNever = attr.field === neverField || attr.operator === neverOperator;
    const evaluated = this.onEvaluate();

    // In live mode a group whose condition is not met should not occupy any
    // layout space. Returning null collapses the container entirely rather
    // than leaving an empty group-container div behind.
    if (store.mode === 'live' && !evaluated) {
      return null;
    }

    if (evaluated) {
      let props = Object.assign({}, this.props);
      props.blockProps.editable = false;
      content = <EditorBlock {...props} />;
    }

    let op: any = {
      '=': 'is',
      '!': "isn't",
      '<': 'is less than',
      '>': 'is greater than',
      '><': `is > ${numberRange[0]}; < ${numberRange[1]};`,
      '>=<=': `is >= ${numberRange[0]}; <= ${numberRange[1]};`,
      '|': 'has',
      '^': "hasn't",
      [neverOperator]: ''
    };

    return (
      <div>
        <div
          className="group-header"
          contentEditable={false}
          draggable={true}
          onClick={this.onSelect.bind(this)}
          onDragStart={this.onDragStart.bind(this)}
        >
          <div className="group-rule">
            <span>Show when </span>
            <span>{isNever ? 'Never' : attr.fieldName || JSON.stringify(attr)}</span>
            {!isNever && <span> {op[attr.operator]} </span>}
            {!isNever && (
              <span>
                {attr.operator === '><' || attr.operator === '>=<=' ? '' : attr.operatorValue}
              </span>
            )}
          </div>
        </div>
        {content}
      </div>
    );
  }

  onEvaluate() {
    let { store, key } = this.props.blockProps;
    if (store.mode !== 'live') {
      return true;
    }
    let entity = Entity.get(key);
    let attr = entity.getData();

    if (attr.field === neverField || attr.operator === neverOperator) {
      return false;
    }

    let data;
    try {
      data = store.data[attr.field].toString();
    } catch {
      return false;
    }

    if (!data) return false; // required for "isn't type logic"
    if (!isArray(data)) {
      data = data !== null ? data.toString() : data;
    }
    let numberRange = this.getNumberRange(attr.operatorValue);

    if (
      typeof attr.operatorValue === 'string' &&
      typeof data === 'string' &&
      attr.operatorValue.indexOf('[') < 0 &&
      data.indexOf('[') >= 0
    ) {
      // hack for old groups expecting option to return a simple string but actually getting a stringified array
      attr.operatorValue = `[\"${attr.operatorValue}\"]`;
    }
    try {
      // hack for option groups multi-select to use has and hasn't options
      let operatorParsedValue = null;
      if (
        typeof attr.operatorValue === 'string' &&
        typeof data === 'string' &&
        attr.operatorValue.indexOf('[') >= 0 &&
        data.indexOf('[') >= 0
      ) {
        operatorParsedValue = JSON.parse(attr.operatorValue);
      }

      switch (attr.operator) {
        case '=':
          if (isArray(data) && isArray(attr.operatorValue)) {
            return attr.operatorValue.every((x) => data.includes(x));
          }
          return data === attr.operatorValue;
        case '!':
          if (isArray(data) && isArray(attr.operatorValue)) {
            return attr.operatorValue.every((x) => !data.includes(x));
          }
          return data !== attr.operatorValue;
        case '<':
          return parseFloat(data) < parseFloat(attr.operatorValue);
        case '>':
          return parseFloat(data) > parseFloat(attr.operatorValue);
        case '><':
          if (numberRange.length >= 2) {
            return numberRange[0] < parseFloat(data) && parseFloat(data) < numberRange[1];
          }
          return false;
        case '>=<=':
          if (numberRange.length >= 2) {
            return numberRange[0] <= parseFloat(data) && parseFloat(data) <= numberRange[1];
          }
          return false;
        case '|':
          if (isArray(data) && isArray(attr.operatorValue)) {
            return attr.operatorValue.every((x) => data.includes(x));
          } else if (operatorParsedValue && isArray(operatorParsedValue)) {
            return operatorParsedValue.every((x) => data.includes(x));
          }
          break;
        case '^':
          if (isArray(data) && isArray(attr.operatorValue)) {
            return attr.operatorValue.every((x) => data.includes(x));
          } else if (operatorParsedValue && isArray(operatorParsedValue)) {
            return operatorParsedValue.every((x) => !data.includes(x));
          }
          break;
        default:
          return false;
      }
    } catch {
      return false;
    }
  }

  onSelect() {
    let {
      block,
      blockProps: { key, store }
    } = this.props;
    store.onSelect(key, block.key);
  }

  onDragStart(e) {
    let { key, store } = this.props.blockProps;
    store.onDragStart(key, e);
  }

  getNumberRange(value) {
    const regex = /[-]?[0-9]\d*(\.\d+)?/g;
    let m;
    let numbers = [];

    while ((m = regex.exec(value)) !== null) {
      // This is necessary to avoid infinite loops with zero-width matches
      if (m.index === regex.lastIndex) regex.lastIndex++;
      numbers.push(m['0']);
    }
    return numbers;
  }
}

export default Group;
